import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Alert,
  ActivityIndicator,
  RefreshControl,
  Modal,
  FlatList,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { useSettlementStore, SettlementFilters, GenerationResult } from '../../stores/settlementStore';
import { SettlementRecord } from '../../types';

const { width: screenWidth } = Dimensions.get('window');
const isTablet = screenWidth > 768;
const isWeb = Platform.OS === 'web';

interface MonthlySettlementScreenProps {
  navigation?: any;
}

export const MonthlySettlementScreen: React.FC<MonthlySettlementScreenProps> = ({ navigation }) => {
  // Store state
  const {
    settlements,
    loading,
    error,
    fetchSettlements,
    generateMonthlySettlements,
    approveSettlement,
    recordPayment,
    getSettlementSummary,
    clearError,
  } = useSettlementStore();

  // UI state
  const [refreshing, setRefreshing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [statusFilter, setStatusFilter] = useState<'all' | SettlementRecord['status']>('all');
  const [searchText, setSearchText] = useState('');
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<SettlementRecord | null>(null);
  const [autoApprove, setAutoApprove] = useState(false);

  // Summary state
  const [summary, setSummary] = useState({
    total_settlements: 0,
    total_rent: 0,
    total_income: 0,
    total_expenses: 0,
    net_balance: 0,
    pending_count: 0,
    approved_count: 0,
    paid_count: 0,
  });

  // Payment form state
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth, statusFilter]);

  const loadData = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      }

      // Build filters
      const filters: SettlementFilters = {
        year: selectedYear,
        month: selectedMonth,
      };

      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      // Load settlements and summary
      await fetchSettlements(filters);
      const summaryData = await getSettlementSummary(selectedYear, selectedMonth);
      setSummary(summaryData);
    } catch (err) {
      console.error('Error loading settlement data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerateSettlements = async () => {
    try {
      Alert.alert(
        'Generate Settlements',
        `Generate settlements for ${getMonthName(selectedMonth)} ${selectedYear}?${
          autoApprove ? '\n\nSettlements will be auto-approved.' : ''
        }`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Generate',
            onPress: async () => {
              try {
                setShowGenerateModal(false);
                
                // Show loading state immediately
                setRefreshing(true);
                
                const result: GenerationResult = await generateMonthlySettlements(
                  selectedYear,
                  selectedMonth,
                  autoApprove
                );

                // Clear filters so new settlements are visible
                setStatusFilter('all');
                setSearchText('');
                
                // Refresh data to show new settlements
                await loadData();
                
                // Calculate totals for better feedback
                const totalDays = result.settlements.reduce((sum, s) => sum + s.total_days, 0);
                const totalBalance = result.settlements.reduce((sum, s) => sum + s.net_balance, 0);
                const positiveBalance = result.settlements.filter(s => s.net_balance > 0).length;
                const negativeBalance = result.settlements.filter(s => s.net_balance < 0).length;

                Alert.alert(
                  '✅ Settlements Created Successfully!',
                  `${result.created} settlement${result.created !== 1 ? 's' : ''} generated for ${getMonthName(selectedMonth)} ${selectedYear}\n\n` +
                    `📊 Summary:\n` +
                    `• Total Days: ${totalDays}\n` +
                    `• Total Balance: AED ${totalBalance.toFixed(2)}\n` +
                    `• Positive Balance: ${positiveBalance} driver${positiveBalance !== 1 ? 's' : ''}\n` +
                    `• Negative Balance: ${negativeBalance} driver${negativeBalance !== 1 ? 's' : ''}\n` +
                    `• Status: ${autoApprove ? 'Approved' : 'Pending Approval'}` +
                    `${result.errors.length > 0 ? `\n\n⚠️ Errors: ${result.errors.length}` : ''}\n\n` +
                    `The settlements are now visible in the list below.`,
                  [{ text: 'OK' }]
                );
              } catch (err: any) {
                setRefreshing(false);
                Alert.alert('❌ Error', err.message || 'Failed to generate settlements');
              }
            },
          },
        ]
      );
    } catch (err) {
      console.error('Error in handleGenerateSettlements:', err);
    }
  };

  const handleApproveSettlement = async (settlement: SettlementRecord) => {
    Alert.alert(
      'Approve Settlement',
      `Approve settlement for ${settlement.driver?.name || 'Unknown Driver'}?\n\nNet Balance: AED ${settlement.net_balance.toFixed(
        2
      )}`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Approve',
          onPress: async () => {
            try {
              await approveSettlement(settlement.id, 'Admin'); // TODO: Get actual user
              Alert.alert('Success', 'Settlement approved successfully');
              loadData();
            } catch (err: any) {
              Alert.alert('Error', err.message || 'Failed to approve settlement');
            }
          },
        },
      ]
    );
  };

  const handleRecordPayment = async () => {
    if (!selectedSettlement) return;

    if (!paymentReference.trim()) {
      Alert.alert('Validation Error', 'Please enter a payment reference');
      return;
    }

    try {
      await recordPayment(selectedSettlement.id, {
        payment_amount: selectedSettlement.outstanding_balance ?? selectedSettlement.net_balance,
        payment_method: 'cash',
        payment_date: paymentDate,
        payment_reference: paymentReference,
        notes: paymentNotes,
      });

      Alert.alert('Success', 'Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentReference('');
      setPaymentNotes('');
      loadData();
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to record payment');
    }
  };

  const handleExportCSV = () => {
    if (!isWeb) {
      Alert.alert('Export Not Available', 'CSV export is only available on web platform');
      return;
    }

    try {
      // Filter settlements
      const filteredSettlements = filterSettlements();

      // Build CSV content
      const headers = [
        'Settlement ID',
        'Driver Name',
        'Vehicle Plate',
        'Period',
        'Period Dates',
        'Total Days',
        'Daily Rate',
        'Company Share',
        'Total Income',
        'Total Expenses',
        'Net Balance',
        'Status',
        'Approved Date',
        'Paid Date',
      ];

      const rows = filteredSettlements.map((s) => [
        s.id,
        s.driver?.name || 'N/A',
        s.vehicle?.license_plate || 'N/A',
        `${s.period_year}-${String(s.period_month).padStart(2, '0')}`,
        `${s.period_start_date || 'N/A'} to ${s.period_end_date || 'N/A'}`,
        s.total_days || 0,
        s.daily_rate?.toFixed(2) || '0.00',
        s.total_rent_charged?.toFixed(2) || '0.00',
        s.total_income?.toFixed(2) || '0.00',
        ((s.total_fines || 0) + (s.total_salik || 0) + (s.total_other_expenses || 0)).toFixed(2),
        s.net_balance?.toFixed(2) || '0.00',
        s.status,
        s.approved_date || 'N/A',
        s.paid_date || 'N/A',
      ]);

      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `settlements_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Alert.alert('Success', 'CSV file downloaded successfully');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      Alert.alert('Error', 'Failed to export CSV');
    }
  };

  const filterSettlements = () => {
    let filtered = [...settlements];

    // Search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.driver?.name?.toLowerCase().includes(search) ||
          s.vehicle?.license_plate?.toLowerCase().includes(search) ||
          s.id.toLowerCase().includes(search)
      );
    }

    return filtered;
  };

  const getStatusColor = (status: SettlementRecord['status']) => {
    switch (status) {
      case 'pending':
        return theme.colors.warning;
      case 'approved':
        return theme.colors.info;
      case 'paid':
        return theme.colors.success;
      case 'disputed':
        return theme.colors.error;
      case 'cancelled':
        return theme.colors.textSecondary;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getMonthName = (month: number) => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months[month - 1];
  };

  const renderSummaryCards = () => (
    <View style={styles.summaryContainer}>
      <View style={styles.summaryCard}>
        <Ionicons name="document-text-outline" size={24} color={theme.colors.primary} />
        <Text style={styles.summaryValue}>{summary.total_settlements}</Text>
        <Text style={styles.summaryLabel}>Total Settlements</Text>
      </View>

      <View style={styles.summaryCard}>
        <Ionicons name="cash-outline" size={24} color={theme.colors.success} />
        <Text style={styles.summaryValue}>AED {(summary.total_rent || 0).toFixed(0)}</Text>
        <Text style={styles.summaryLabel}>Total Company Share</Text>
      </View>

      <View style={styles.summaryCard}>
        <Ionicons name="trending-up-outline" size={24} color={theme.colors.info} />
        <Text style={styles.summaryValue}>AED {(summary.total_income || 0).toFixed(0)}</Text>
        <Text style={styles.summaryLabel}>Total Income</Text>
      </View>

      <View style={styles.summaryCard}>
        <Ionicons name="wallet-outline" size={24} color={theme.colors.warning} />
        <Text style={styles.summaryValue}>AED {(summary.net_balance || 0).toFixed(0)}</Text>
        <Text style={styles.summaryLabel}>Net Balance</Text>
      </View>
    </View>
  );

  const renderStatusFilter = () => (
    <View style={styles.filterContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {['all', 'pending', 'approved', 'paid', 'disputed'].map((status) => (
          <TouchableOpacity
            key={status}
            style={[styles.filterChip, statusFilter === status && styles.filterChipActive]}
            onPress={() => setStatusFilter(status as any)}
          >
            <Text
              style={[
                styles.filterChipText,
                statusFilter === status && styles.filterChipTextActive,
              ]}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {status !== 'all' &&
                ` (${
                  status === 'pending'
                    ? summary.pending_count
                    : status === 'approved'
                    ? summary.approved_count
                    : status === 'paid'
                    ? summary.paid_count
                    : 0
                })`}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSettlementItem = ({ item }: { item: SettlementRecord }) => (
    <TouchableOpacity
      style={styles.settlementCard}
      onPress={() => {
        if (navigation) {
          navigation.navigate('SettlementDetail', { settlementId: item.id });
        } else {
          setSelectedSettlement(item);
        }
      }}
    >
      <View style={styles.settlementHeader}>
        <View style={styles.settlementHeaderLeft}>
          <Text style={styles.driverName}>{item.driver?.name || 'Unknown Driver'}</Text>
          <Text style={styles.vehiclePlate}>{item.vehicle?.license_plate || 'N/A'}</Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusText}>{item.status.toUpperCase()}</Text>
        </View>
      </View>

      <View style={styles.settlementDetails}>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Period:</Text>
          <Text style={styles.detailValue}>
            {getMonthName(item.period_month)} {item.period_year}
          </Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Days:</Text>
          <Text style={styles.detailValue}>{item.total_days || 0} days</Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Company Share:</Text>
          <Text style={styles.detailValue}>AED {item.total_rent_charged?.toFixed(2) || '0.00'}</Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Income:</Text>
          <Text style={[styles.detailValue, { color: theme.colors.success }]}>
            AED {item.total_income?.toFixed(2) || '0.00'}
          </Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Expenses:</Text>
          <Text style={[styles.detailValue, { color: theme.colors.error }]}>
            AED {((item.total_fines || 0) + (item.total_salik || 0) + (item.total_other_expenses || 0)).toFixed(2)}
          </Text>
        </View>
        <View style={[styles.detailRow, styles.netBalanceRow]}>
          <Text style={styles.netBalanceLabel}>Net Balance:</Text>
          <Text
            style={[
              styles.netBalanceValue,
              { color: item.net_balance >= 0 ? theme.colors.success : theme.colors.error },
            ]}
          >
            AED {item.net_balance?.toFixed(2) || '0.00'}
          </Text>
        </View>
      </View>

      <View style={styles.settlementActions}>
        {item.status === 'pending' && (
          <TouchableOpacity
            style={[styles.actionButton, styles.approveButton]}
            onPress={() => handleApproveSettlement(item)}
          >
            <Ionicons name="checkmark-circle-outline" size={20} color="#fff" />
            <Text style={styles.actionButtonText}>Approve</Text>
          </TouchableOpacity>
        )}
        {item.status === 'approved' && (
          <TouchableOpacity
            style={[styles.actionButton, styles.payButton]}
            onPress={() => {
              setSelectedSettlement(item);
              setShowPaymentModal(true);
            }}
          >
            <Ionicons name="wallet-outline" size={20} color="#fff" />
            <Text style={styles.actionButtonText}>Record Payment</Text>
          </TouchableOpacity>
        )}
        {item.status === 'paid' && (
          <View style={styles.paidInfo}>
            <Ionicons name="checkmark-done-circle" size={20} color={theme.colors.success} />
            <Text style={styles.paidText}>
              Paid on {item.paid_date ? new Date(item.paid_date).toLocaleDateString() : 'N/A'}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  const renderPaymentModal = () => (
    <Modal
      visible={showPaymentModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowPaymentModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Record Payment</Text>
            <TouchableOpacity onPress={() => setShowPaymentModal(false)}>
              <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
            </TouchableOpacity>
          </View>

          {selectedSettlement && (
            <View style={styles.modalBody}>
              <Text style={styles.modalSubtitle}>
                {selectedSettlement.driver?.name} - {selectedSettlement.vehicle?.license_plate}
              </Text>
              <Text style={styles.modalAmount}>
                Amount: AED {selectedSettlement.net_balance?.toFixed(2)}
              </Text>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Payment Date</Text>
                <TextInput
                  style={styles.input}
                  value={paymentDate}
                  onChangeText={setPaymentDate}
                  placeholder="YYYY-MM-DD"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Payment Reference *</Text>
                <TextInput
                  style={styles.input}
                  value={paymentReference}
                  onChangeText={setPaymentReference}
                  placeholder="Transaction ID, Check Number, etc."
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Notes (Optional)</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={paymentNotes}
                  onChangeText={setPaymentNotes}
                  placeholder="Additional notes..."
                  multiline
                  numberOfLines={3}
                />
              </View>

              <TouchableOpacity style={styles.submitButton} onPress={handleRecordPayment}>
                <Text style={styles.submitButtonText}>Record Payment</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );

  const renderGenerateModal = () => (
    <Modal
      visible={showGenerateModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowGenerateModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Generate Settlements</Text>
            <TouchableOpacity onPress={() => setShowGenerateModal(false)}>
              <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
            </TouchableOpacity>
          </View>

          <View style={styles.modalBody}>
            <Text style={styles.modalSubtitle}>
              Generate settlements for {getMonthName(selectedMonth)} {selectedYear}
            </Text>

            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setAutoApprove(!autoApprove)}
            >
              <Ionicons
                name={autoApprove ? 'checkbox' : 'square-outline'}
                size={24}
                color={theme.colors.primary}
              />
              <Text style={styles.checkboxLabel}>Auto-approve settlements</Text>
            </TouchableOpacity>

            <View style={styles.warningBox}>
              <Ionicons name="information-circle" size={24} color={theme.colors.warning} />
              <Text style={styles.warningText}>
                This will create settlement records for all assignments with status "pending_settlement"
                for the selected month.
              </Text>
            </View>

            <TouchableOpacity style={styles.submitButton} onPress={handleGenerateSettlements}>
              <Text style={styles.submitButtonText}>Generate Settlements</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const filteredSettlements = filterSettlements();

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>Monthly Settlements</Text>
          <Text style={styles.headerSubtitle}>
            {getMonthName(selectedMonth)} {selectedYear}
          </Text>
        </View>
        <View style={styles.headerRight}>
          <TouchableOpacity style={styles.iconButton} onPress={() => loadData(true)}>
            <Ionicons name="refresh" size={24} color={theme.colors.primary} />
          </TouchableOpacity>
          {isWeb && (
            <TouchableOpacity style={styles.iconButton} onPress={handleExportCSV}>
              <Ionicons name="download-outline" size={24} color={theme.colors.primary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Month/Year Selector */}
      <View style={styles.selectorContainer}>
        <TouchableOpacity
          style={styles.selectorButton}
          onPress={() => setShowYearPicker(!showYearPicker)}
        >
          <Text style={styles.selectorText}>{selectedYear}</Text>
          <Ionicons name="chevron-down" size={20} color={theme.colors.textPrimary} />
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.selectorButton}
          onPress={() => setShowMonthPicker(!showMonthPicker)}
        >
          <Text style={styles.selectorText}>{getMonthName(selectedMonth)}</Text>
          <Ionicons name="chevron-down" size={20} color={theme.colors.textPrimary} />
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.generateButton}
          onPress={() => setShowGenerateModal(true)}
        >
          <Ionicons name="add-circle-outline" size={20} color="#fff" />
          <Text style={styles.generateButtonText}>Generate</Text>
        </TouchableOpacity>
      </View>

      {/* Year Picker */}
      {showYearPicker && (
        <View style={styles.pickerContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
              <TouchableOpacity
                key={year}
                style={[styles.pickerItem, selectedYear === year && styles.pickerItemActive]}
                onPress={() => {
                  setSelectedYear(year);
                  setShowYearPicker(false);
                }}
              >
                <Text
                  style={[
                    styles.pickerItemText,
                    selectedYear === year && styles.pickerItemTextActive,
                  ]}
                >
                  {year}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Month Picker */}
      {showMonthPicker && (
        <View style={styles.pickerContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <TouchableOpacity
                key={month}
                style={[styles.pickerItem, selectedMonth === month && styles.pickerItemActive]}
                onPress={() => {
                  setSelectedMonth(month);
                  setShowMonthPicker(false);
                }}
              >
                <Text
                  style={[
                    styles.pickerItemText,
                    selectedMonth === month && styles.pickerItemTextActive,
                  ]}
                >
                  {getMonthName(month)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {/* Summary Cards */}
      {renderSummaryCards()}

      {/* Status Filter */}
      {renderStatusFilter()}

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color={theme.colors.textSecondary} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by driver, vehicle, or ID..."
          value={searchText}
          onChangeText={setSearchText}
        />
        {searchText.length > 0 && (
          <TouchableOpacity onPress={() => setSearchText('')}>
            <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
          </TouchableOpacity>
        )}
      </View>

      {/* Settlements List */}
      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading settlements...</Text>
        </View>
      ) : filteredSettlements.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="document-text-outline" size={64} color={theme.colors.textSecondary} />
          <Text style={styles.emptyText}>No settlements found</Text>
          <Text style={styles.emptySubtext}>
            {statusFilter === 'all'
              ? 'Generate settlements for this period'
              : `No ${statusFilter} settlements for this period`}
          </Text>
        </View>
      ) : (
        <FlatList
          data={filteredSettlements}
          keyExtractor={(item) => item.id}
          renderItem={renderSettlementItem}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => loadData(true)}
              colors={[theme.colors.primary]}
            />
          }
          contentContainerStyle={styles.listContent}
        />
      )}

      {/* Modals */}
      {renderPaymentModal()}
      {renderGenerateModal()}

      {/* Error Message */}
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={clearError}>
            <Ionicons name="close" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerLeft: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  headerRight: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    padding: 8,
  },
  selectorContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    backgroundColor: theme.colors.surface,
  },
  selectorButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.background,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  selectorText: {
    fontSize: 16,
    color: theme.colors.textPrimary,
    fontWeight: '500',
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.primary,
    borderRadius: 8,
    gap: 8,
  },
  generateButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  pickerContainer: {
    backgroundColor: theme.colors.surface,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  pickerItem: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    marginHorizontal: 4,
    borderRadius: 20,
  },
  pickerItemActive: {
    backgroundColor: theme.colors.primary,
  },
  pickerItemText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  pickerItemTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  summaryContainer: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    backgroundColor: theme.colors.surface,
  },
  summaryCard: {
    flex: 1,
    padding: 16,
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginTop: 8,
  },
  summaryLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 4,
    textAlign: 'center',
  },
  filterContainer: {
    paddingVertical: 12,
    paddingLeft: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  filterChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  filterChipActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterChipText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  filterChipTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.surface,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 8,
    textAlign: 'center',
  },
  listContent: {
    padding: 16,
  },
  settlementCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  settlementHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  settlementHeaderLeft: {
    flex: 1,
  },
  driverName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  vehiclePlate: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  settlementDetails: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
    paddingTop: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  detailLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
  },
  netBalanceRow: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  netBalanceLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  netBalanceValue: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  settlementActions: {
    marginTop: 12,
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  approveButton: {
    backgroundColor: theme.colors.success,
  },
  payButton: {
    backgroundColor: theme.colors.primary,
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  paidInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paidText: {
    fontSize: 14,
    color: theme.colors.success,
    fontWeight: '500',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    width: isTablet ? 600 : '90%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  modalBody: {
    padding: 16,
  },
  modalSubtitle: {
    fontSize: 16,
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  modalAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginBottom: 24,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  input: {
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: theme.colors.primary,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 16,
  },
  checkboxLabel: {
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    backgroundColor: theme.colors.warning + '20',
    borderRadius: 8,
    marginBottom: 16,
  },
  warningText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 20,
  },
  errorBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.error,
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorText: {
    flex: 1,
    color: '#fff',
    fontSize: 14,
  },
});

export default MonthlySettlementScreen;
